import ActivityReview from './ActivityReview';
import AssetIdentityHeader from './AssetIdentityHeader';
import SoftwareInventory from './SoftwareInventory';
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import bitdefenderService from '../services/bitdefenderService';
import screenconnectService from '../services/screenconnectService';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { RmmHardwareTab, RmmSoftwareTab, RmmServicesTab, RmmAlertsTab, RmmActivitiesTab, RmmPatchesTab } from './RmmAssetTabs';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Separator } from './ui/separator';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Package, Calendar, MapPin, User, Hash, Settings, 
  Ticket, RefreshCw, ExternalLink, CheckCircle, XCircle, 
  AlertCircle, Activity, Shield, Monitor, FileText, 
  HardDrive, Cpu, Network, Server, Power, Terminal,
  Download, Clock, AlertTriangle, Play, PlayCircle, StopCircle
} from 'lucide-react';
import { toast } from 'sonner';
import { formatInCST } from '../utils/timezone';

export default function AssetDetailDialog({ asset, open, onOpenChange, onUpdate }) {
  const navigate = useNavigate();
  const [relatedTickets, setRelatedTickets] = useState([]);
  const [integrationData, setIntegrationData] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({
    ninjarmm_device_id: '',
    bitdefender_endpoint_id: '',
    screenconnect_session_id: ''
  });
  
  // NinjaRMM deep data states
  const [ninjaData, setNinjaData] = useState({
    software: [],
    activities: [],
    alerts: [],
    networkInterfaces: [],
    processors: [],
    disks: [],
    services: [],
    dashboardUrl: ''
  });
  const [ninjaLoading, setNinjaLoading] = useState({
    software: false,
    activities: false,
    alerts: false,
    system: false,
    services: false
  });
  const [rebooting, setRebooting] = useState(false);
  const [sectionTimestamps, setSectionTimestamps] = useState({});
  const [syncOverride, setSyncOverride] = useState(null);
  const [bitdefenderData, setBitdefenderData] = useState(null);
  const [bitdefenderLoading, setBitdefenderLoading] = useState(false);
  const [screenconnectCreating, setScreenconnectCreating] = useState(false);
  const [managingService, setManagingService] = useState(null);

  useEffect(() => {
    if (open && asset) {
      loadRelatedTickets();
      loadBitdefenderData();
      setIntegrationData(asset.integration_data || {});
      setEditForm({
        ninjarmm_device_id: asset.ninjarmm_device_id || '',
        bitdefender_endpoint_id: asset.bitdefender_endpoint_id || '',
        screenconnect_session_id: asset.screenconnect_session_id || ''
      });
      
      // Load sync override settings
      if (asset.ninjarmm_device_id) {
        api.get(`/assets/${asset.id}/sync-override`).then(res => setSyncOverride(res.data)).catch(() => setSyncOverride({ mode: 'global' }));
      }
      
      // Fetch fresh asset data to get ninjarmm_data (list API doesn't include it)
      const loadFreshAssetData = async () => {
        try {
          const response = await api.get(`/assets/${asset.id}`);
          const freshAsset = response.data;
          
          // Update NinjaRMM data from fresh asset
          if (freshAsset.ninjarmm_data) {
            setNinjaData({
              software: freshAsset.ninjarmm_data.software || [],
              activities: freshAsset.ninjarmm_data.activities || [],
              alerts: freshAsset.ninjarmm_data.alerts || [],
              networkInterfaces: freshAsset.ninjarmm_data.networkInterfaces || [],
              processors: freshAsset.ninjarmm_data.processors || [],
              disks: freshAsset.ninjarmm_data.disks || [],
              services: freshAsset.ninjarmm_data.services || [],
              dashboardUrl: freshAsset.ninjarmm_data.dashboardUrl || ''
            });
            setSectionTimestamps(freshAsset.ninjarmm_data.section_timestamps || {});
          } else {
            // Reset NinjaRMM data when dialog opens if no saved data
            setNinjaData({
              software: [],
              activities: [],
              alerts: [],
              networkInterfaces: [],
              processors: [],
              disks: [],
              services: [],
              dashboardUrl: ''
            });
            setSectionTimestamps({});
          }
        } catch (error) {
          console.error('Failed to load fresh asset data:', error);
          // Fallback to asset prop data if fetch fails
          if (asset.ninjarmm_data) {
            setNinjaData({
              software: asset.ninjarmm_data.software || [],
              activities: asset.ninjarmm_data.activities || [],
              alerts: asset.ninjarmm_data.alerts || [],
              networkInterfaces: asset.ninjarmm_data.networkInterfaces || [],
              processors: asset.ninjarmm_data.processors || [],
              disks: asset.ninjarmm_data.disks || [],
              services: asset.ninjarmm_data.services || [],
              dashboardUrl: asset.ninjarmm_data.dashboardUrl || ''
            });
            setSectionTimestamps(asset.ninjarmm_data.section_timestamps || {});
          }
        }
      };
      
      loadFreshAssetData();
    }
  }, [open, asset]);

  // NinjaRMM data fetching functions
  const loadNinjaSoftware = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, software: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/software`);
      const software = response.data.software || response.data || [];
      const softwareArray = Array.isArray(software) ? software : [];
      setNinjaData(prev => ({ ...prev, software: softwareArray }));
      
      // Save to database (merges with existing data)
      const saveRes = await api.post(`/assets/${asset.id}/save-ninjarmm-data`, { software: softwareArray });
      setSectionTimestamps(saveRes.data.section_timestamps || {});
    } catch (error) {
      console.error('Failed to load software inventory:', error);
      toast.error('Failed to load software inventory');
      setNinjaData(prev => ({ ...prev, software: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, software: false }));
    }
  };

  const loadNinjaActivities = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, activities: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/activities`);
      const activitiesData = response.data.activities?.activities || response.data.activities || response.data || [];
      const activitiesArray = Array.isArray(activitiesData) ? activitiesData : [];
      setNinjaData(prev => ({ ...prev, activities: activitiesArray }));
      
      const saveRes = await api.post(`/assets/${asset.id}/save-ninjarmm-data`, { activities: activitiesArray });
      setSectionTimestamps(saveRes.data.section_timestamps || {});
    } catch (error) {
      console.error('Failed to load activities:', error);
      toast.error('Failed to load device activities');
      setNinjaData(prev => ({ ...prev, activities: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, activities: false }));
    }
  };

  const loadNinjaAlerts = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, alerts: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/alerts`);
      const alerts = response.data.alerts || response.data || [];
      setNinjaData(prev => ({ ...prev, alerts: Array.isArray(alerts) ? alerts : [] }));
    } catch (error) {
      console.error('Failed to load alerts:', error);
      toast.error('Failed to load device alerts');
      setNinjaData(prev => ({ ...prev, alerts: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, alerts: false }));
    }
  };

  const loadNinjaSystemInfo = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, system: true }));
    try {
      const [networkRes, processorsRes, disksRes, dashboardRes] = await Promise.all([
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/network-interfaces`),
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/processors`),
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/disks`),
        api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/dashboard-url`)
      ]);
      
      const networkInterfaces = networkRes.data.interfaces || networkRes.data || [];
      const processors = processorsRes.data.processors || processorsRes.data || [];
      const disks = disksRes.data.disks || disksRes.data || [];
      
      const systemData = {
        networkInterfaces: Array.isArray(networkInterfaces) ? networkInterfaces : [],
        processors: Array.isArray(processors) ? processors : [],
        disks: Array.isArray(disks) ? disks : [],
        dashboardUrl: dashboardRes.data.url || ''
      };
      
      setNinjaData(prev => ({ ...prev, ...systemData }));
      
      const saveRes = await api.post(`/assets/${asset.id}/save-ninjarmm-data`, systemData);
      setSectionTimestamps(saveRes.data.section_timestamps || {});
    } catch (error) {
      console.error('Failed to load system info:', error);
      toast.error('Failed to load system information');
      setNinjaData(prev => ({
        ...prev,
        networkInterfaces: [],
        processors: [],
        disks: [],
        dashboardUrl: ''
      }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, system: false }));
    }
  };

  const loadNinjaServices = async () => {
    if (!asset?.ninjarmm_device_id) return;
    setNinjaLoading(prev => ({ ...prev, services: true }));
    try {
      const response = await api.get(`/ninjarmm/device/${asset.ninjarmm_device_id}/services`);
      const services = response.data.services || response.data || [];
      const servicesArray = Array.isArray(services) ? services : [];
      setNinjaData(prev => ({ ...prev, services: servicesArray }));
      
      const saveRes = await api.post(`/assets/${asset.id}/save-ninjarmm-data`, { services: servicesArray });
      setSectionTimestamps(saveRes.data.section_timestamps || {});
    } catch (error) {
      console.error('Failed to load services:', error);
      toast.error('Failed to load Windows services');
      setNinjaData(prev => ({ ...prev, services: [] }));
    } finally {
      setNinjaLoading(prev => ({ ...prev, services: false }));
    }
  };

  const handleRebootDevice = async (mode = 'NORMAL') => {
    if (!asset?.ninjarmm_device_id) return;
    
    const confirmMsg = mode === 'SAFE' 
      ? 'Reboot device in Safe Mode?' 
      : mode === 'SAFE_WITH_NETWORKING'
      ? 'Reboot device in Safe Mode with Networking?'
      : 'Reboot device now?';
    
    if (!window.confirm(confirmMsg)) return;
    
    setRebooting(true);
    try {
      await api.post(`/ninjarmm/device/${asset.ninjarmm_device_id}/reboot?mode=${mode}`);
      toast.success(`Reboot command sent successfully (${mode} mode)`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send reboot command');
    } finally {
      setRebooting(false);
    }
  };


  const handleManageService = async (serviceName, action) => {
    if (!asset?.ninjarmm_device_id) return;
    
    const confirmMsg = `${action.charAt(0).toUpperCase() + action.slice(1)} the "${serviceName}" service?`;
    if (!window.confirm(confirmMsg)) return;
    
    setManagingService(`${serviceName}-${action}`);
    try {
      await api.post(`/ninjarmm/device/${asset.ninjarmm_device_id}/service/${encodeURIComponent(serviceName)}/manage?action=${action}`);
      toast.success(`Service ${action} command sent successfully`);
      
      // Reload services after a delay to see updated status
      setTimeout(() => {
        loadNinjaServices();
      }, 2000);
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${action} service`);
    } finally {
      setManagingService(null);
    }
  };


  const loadRelatedTickets = async () => {
    if (!asset) return;
    
    setLoading(true);
    try {
      const response = await api.get(`/assets/${asset.id}/tickets`);
      setRelatedTickets(response.data);
    } catch (error) {
      console.error('Failed to load related tickets');
    } finally {
      setLoading(false);
    }
  };

  const loadBitdefenderData = async () => {
    if (!asset || !asset.id) {
// removed console statement
      return;
    }
    
    setBitdefenderLoading(true);
    try {
// removed console statement
      const response = await bitdefenderService.getAssetEndpoint(asset.id);
// removed console statement
      if (response && response.endpoint) {
        setBitdefenderData(response.endpoint);
// removed console statement
      } else {
        setBitdefenderData(null);
// removed console statement
      }
    } catch (error) {
      console.error('Failed to load BitDefender data:', error);
      setBitdefenderData(null);
    } finally {
      setBitdefenderLoading(false);
    }
  };


  const handleCreateScreenConnectSession = async () => {
    setScreenconnectCreating(true);
    try {
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      const result = await screenconnectService.createSession(
        asset.id,
        asset.name,
        user.full_name || user.email
      );
      
      if (result.success) {
        toast.success('Remote session created!');
        
        // Update local asset state
        if (onUpdate) {
          onUpdate({
            ...asset,
            screenconnect_session_id: result.session_id,
            screenconnect_session_status: 'active',
            screenconnect_guest_url: result.guest_url,
            screenconnect_host_url: result.host_url
          });
        }
        
        // Open the host URL automatically
        window.open(result.host_url, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error('Failed to create remote session');
    } finally {
      setScreenconnectCreating(false);
    }
  };



  const handleSyncIntegrations = async () => {
    setSyncing(true);
    try {
      // Sync from NinjaRMM if configured
      if (asset.ninjarmm_device_id) {
        const response = await api.post(`/assets/${asset.id}/sync-ninjarmm`);
        setIntegrationData(response.data.integration_data || {});
        toast.success('NinjaRMM data synced successfully');
        if (onUpdate) onUpdate();
      } else {
        toast.error('No NinjaRMM device ID configured');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync integrations');
    } finally {
      setSyncing(false);
    }
  };

  const handleSaveIntegrationIds = async () => {
    try {
      await api.put(`/assets/${asset.id}`, editForm);
      toast.success('Integration IDs updated');
      setEditMode(false);
      if (onUpdate) onUpdate();
      // Reload the asset data
      const response = await api.get(`/assets/${asset.id}`);
      setIntegrationData(response.data.integration_data || {});
    } catch (error) {
      toast.error('Failed to update integration IDs');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case 'maintenance':
        return <AlertCircle className="h-4 w-4 text-yellow-600" />;
      case 'retired':
        return <XCircle className="h-4 w-4 text-gray-600" />;
      default:
        return <XCircle className="h-4 w-4 text-red-600" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800';
      case 'maintenance':
        return 'bg-yellow-100 text-yellow-800';
      case 'retired':
        return 'bg-gray-100 text-gray-800';
      default:
        return 'bg-red-100 text-red-800';
    }
  };

  const getIntegrationStatus = (integrationName) => {
    // Cached inventory cannot establish a current device link.
    if (integrationName === 'ninjarmm' && !asset?.ninjarmm_device_id) {
      return { status: 'not_configured', label: 'Not Configured', color: 'bg-gray-100 text-gray-800' };
    }
    // Check if the integration has an ID configured
    if (integrationName === 'ninjarmm' && asset?.ninjarmm_device_id) {
      return { status: 'configured', label: 'Configured', color: 'bg-green-100 text-green-800' };
    }
    if (integrationName === 'bitdefender' && asset?.bitdefender_endpoint_id) {
      return { status: 'configured', label: 'Configured', color: 'bg-green-100 text-green-800' };
    }
    if (integrationName === 'screenconnect' && asset?.screenconnect_session_id) {
      return { status: 'configured', label: 'Configured', color: 'bg-green-100 text-green-800' };
    }
    
    // Fall back to integration_data if available
    const data = integrationData?.[integrationName];
    if (!data) return { status: 'not_configured', label: 'Not Configured', color: 'bg-gray-100 text-gray-800' };
    
    switch (data.status) {
      case 'connected':
        return { status: 'connected', label: 'Connected', color: 'bg-green-100 text-green-800' };
      case 'ready':
        return { status: 'ready', label: 'Ready to Sync', color: 'bg-blue-100 text-blue-800' };
      case 'error':
        return { status: 'error', label: 'Error', color: 'bg-red-100 text-red-800' };
      default:
        return { status: 'not_configured', label: 'Not Configured', color: 'bg-gray-100 text-gray-800' };
    }
  };

  const handleOpenFullPage = () => {
    navigate(`/assets/${asset.id}`);
    onOpenChange(false);
  };

  if (!asset) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl max-h-[95vh] overflow-y-auto" data-testid="asset-detail-dialog">
        <DialogHeader>
          <div className="flex items-center justify-between">
            <DialogTitle className="flex flex-wrap items-center gap-3 min-w-0">
              <Package className="h-6 w-6" />
              <span>{asset.name}</span>
              <Badge className={getStatusColor(asset.status)}>
                {getStatusIcon(asset.status)}
                <span className="ml-1 capitalize">{asset.status}</span>
              </Badge>
            </DialogTitle>
            <Button variant="outline" size="sm" onClick={handleOpenFullPage} data-testid="open-full-page-btn">
              <ExternalLink className="h-4 w-4 mr-2" />
              Open Full Page
            </Button>
          </div>
        </DialogHeader>

        <AssetIdentityHeader key={asset.id} asset={asset} networkInterfaces={ninjaData.networkInterfaces} />

        <Tabs defaultValue="overview" className="mt-4">
          <div>
            <TabsList className="grid h-auto w-full grid-cols-2 sm:grid-cols-5 gap-1">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="rmm-hardware" disabled={!asset.ninjarmm_device_id}>Hardware</TabsTrigger>
              <TabsTrigger value="rmm-software" disabled={!asset.ninjarmm_device_id}>Software</TabsTrigger>
              <TabsTrigger value="rmm-services" disabled={!asset.ninjarmm_device_id}>Services</TabsTrigger>
              <TabsTrigger value="rmm-alerts" disabled={!asset.ninjarmm_device_id}>Alerts</TabsTrigger>
              <TabsTrigger value="rmm-activities" disabled={!asset.ninjarmm_device_id}>Activities</TabsTrigger>
              <TabsTrigger value="integrations">Integrations</TabsTrigger>
              <TabsTrigger value="tickets">Tickets ({relatedTickets.length})</TabsTrigger>
              <TabsTrigger value="tools" disabled={!asset.ninjarmm_device_id}>Tools</TabsTrigger>
              <TabsTrigger value="rmm-patches" disabled={!asset.ninjarmm_device_id}>Patches</TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Asset Information</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-gray-500">Asset Name</Label>
                      <p className="font-semibold">{asset.name}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Type</Label>
                      <p className="capitalize">{asset.asset_type || 'Device'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Serial Number</Label>
                      <p className="font-mono text-sm">{asset.serial_number || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Model</Label>
                      <p>{asset.model || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Manufacturer</Label>
                      <p>{asset.manufacturer || 'N/A'}</p>
                    </div>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <Label className="text-sm text-gray-500">Customer</Label>
                      <p className="font-semibold">{asset.customer_name || 'N/A'}</p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Location</Label>
                      <p className="flex items-center gap-1">
                        <MapPin className="h-4 w-4 text-gray-400" />
                        {asset.location || 'Not specified'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Purchase Date</Label>
                      <p className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {asset.purchase_date ? formatInCST(asset.purchase_date, 'MMM d, yyyy') : 'N/A'}
                      </p>
                    </div>
                    <div>
                      <Label className="text-sm text-gray-500">Warranty Expiry</Label>
                      <p className="flex items-center gap-1">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {asset.warranty_expiry ? formatInCST(asset.warranty_expiry, 'MMM d, yyyy') : 'N/A'}
                      </p>
                    </div>
                  </div>
                </div>

                {asset.notes && (
                  <div className="mt-6">
                    <Label className="text-sm text-gray-500">Notes</Label>
                    <p className="mt-1 p-3 bg-gray-50 rounded border border-gray-200 whitespace-pre-wrap">
                      {asset.notes}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Integrations Tab */}
          <TabsContent value="integrations" className="space-y-4">
            {/* Warning for assets with NinjaRMM but no BitDefender */}
            {asset.ninjarmm_device_id && !asset.bitdefender_endpoint_id && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-red-900">Security Alert: No Antivirus Protection</h4>
                  <p className="text-sm text-red-800 mt-1">
                    This asset is monitored via NinjaRMM but has no BitDefender endpoint protection. 
                    Go to the BitDefender dashboard and sync endpoints to enable antivirus monitoring.
                  </p>
                </div>
              </div>
            )}
            
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-gray-600">
                Connect this asset to external monitoring and management systems
              </p>
              <div className="flex gap-2">
                {!editMode ? (
                  <Button size="sm" variant="outline" onClick={() => setEditMode(true)}>
                    <Settings className="h-4 w-4 mr-2" />
                    Configure IDs
                  </Button>
                ) : (
                  <>
                    <Button size="sm" variant="outline" onClick={() => setEditMode(false)}>
                      Cancel
                    </Button>
                    <Button size="sm" onClick={handleSaveIntegrationIds}>
                      Save IDs
                    </Button>
                  </>
                )}
                <Button 
                  size="sm" 
                  onClick={handleSyncIntegrations} 
                  disabled={syncing}
                  data-testid="sync-integrations-btn"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${syncing ? 'animate-spin' : ''}`} />
                  {syncing ? 'Syncing...' : 'Sync Now'}
                </Button>
              </div>
            </div>

            {/* NinjaRMM Integration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    NinjaRMM
                  </CardTitle>
                  <Badge className={getIntegrationStatus('ninjarmm').color}>
                    {getIntegrationStatus('ninjarmm').label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <div>
                    <Label>NinjaRMM Device ID</Label>
                    <Input
                      value={editForm.ninjarmm_device_id}
                      onChange={(e) => setEditForm({...editForm, ninjarmm_device_id: e.target.value})}
                      placeholder="Enter NinjaRMM device ID"
                      data-testid="ninjarmm-id-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Link this asset to a NinjaRMM monitored device</p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-gray-500">Device ID</Label>
                    <p className="font-mono text-sm">{asset.ninjarmm_device_id || 'Not configured'}</p>
                  </div>
                )}

                {integrationData?.ninjarmm?.status === 'connected' && integrationData?.ninjarmm?.data && (
                  <>
                    {integrationData.ninjarmm.mocked && (
                      <div className="mb-3 p-2 bg-yellow-50 border border-yellow-200 rounded text-xs text-yellow-800">
                        ⚠️ MOCKED DATA - Replace with real NinjaRMM API when authentication is configured
                      </div>
                    )}
                    <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded">
                      <div>
                        <Label className="text-xs text-gray-500">Status</Label>
                        <Badge className={integrationData.ninjarmm.data.online ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"}>
                          {integrationData.ninjarmm.data.online ? 'Online' : 'Offline'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">System Name</Label>
                        <p className="text-sm font-medium">{integrationData.ninjarmm.data.system_name || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">OS</Label>
                        <p className="text-sm">{integrationData.ninjarmm.data.os_name || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Node Class</Label>
                        <p className="text-sm capitalize">{integrationData.ninjarmm.data.node_class?.toLowerCase().replace('_', ' ') || 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Last Contact</Label>
                        <p className="text-sm">{integrationData.ninjarmm.data.last_contact ? formatInCST(integrationData.ninjarmm.data.last_contact, 'MMM d, h:mm a') : 'N/A'}</p>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">DNS Name</Label>
                        <p className="text-sm font-mono text-xs">{integrationData.ninjarmm.data.dns_name || 'N/A'}</p>
                      </div>
                      
                      {/* Resource usage metrics */}
                      {integrationData.ninjarmm.data.cpu_usage && (
                        <>
                          <div className="col-span-3 border-t border-gray-300 my-2"></div>
                          <div>
                            <Label className="text-xs text-gray-500">CPU Usage</Label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-blue-600 h-2 rounded-full transition-all"
                                  style={{ width: `${integrationData.ninjarmm.data.cpu_usage}%` }}
                                />
                              </div>
                              <span className="text-sm font-semibold">{integrationData.ninjarmm.data.cpu_usage}%</span>
                            </div>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Memory Usage</Label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-green-600 h-2 rounded-full transition-all"
                                  style={{ width: `${integrationData.ninjarmm.data.memory_usage}%` }}
                                />
                              </div>
                              <span className="text-sm font-semibold">{integrationData.ninjarmm.data.memory_usage}%</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">{integrationData.ninjarmm.data.memory_total_gb} GB total</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Disk Usage</Label>
                            <div className="flex items-center gap-2">
                              <div className="flex-1 bg-gray-200 rounded-full h-2">
                                <div 
                                  className="bg-orange-600 h-2 rounded-full transition-all"
                                  style={{ width: `${integrationData.ninjarmm.data.disk_usage}%` }}
                                />
                              </div>
                              <span className="text-sm font-semibold">{integrationData.ninjarmm.data.disk_usage}%</span>
                            </div>
                            <p className="text-xs text-gray-400 mt-1">{integrationData.ninjarmm.data.disk_total_gb} GB total</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">Uptime</Label>
                            <p className="text-sm font-semibold">{integrationData.ninjarmm.data.uptime_hours} hours</p>
                            <p className="text-xs text-gray-400">{Math.floor(integrationData.ninjarmm.data.uptime_hours / 24)} days</p>
                          </div>
                          <div>
                            <Label className="text-xs text-gray-500">CPU Cores</Label>
                            <p className="text-sm font-semibold">{integrationData.ninjarmm.data.cpu_cores} cores</p>
                          </div>
                        </>
                      )}
                    </div>
                  </>
                )}

                {!asset.ninjarmm_device_id && !editMode && (
                  <p className="text-sm text-gray-500 italic">
                    Configure a NinjaRMM Device ID to enable monitoring data
                  </p>
                )}

                {/* Per-Asset Sync Override */}
                {asset.ninjarmm_device_id && (
                  <div className="border-t pt-3 mt-3" data-testid="sync-override-section">
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-sm font-medium flex items-center gap-1.5">
                        <RefreshCw className="h-3.5 w-3.5" /> Auto-Sync Schedule
                      </Label>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      {[
                        { value: 'global', label: 'Use Global' },
                        { value: 'custom', label: 'Custom' },
                        { value: 'excluded', label: 'Excluded' }
                      ].map(opt => (
                        <button
                          key={opt.value}
                          type="button"
                          onClick={async () => {
                            try {
                              const res = await api.put(`/assets/${asset.id}/sync-override`, { mode: opt.value, custom_interval_minutes: opt.value === 'custom' ? (syncOverride?.custom_interval_minutes || 15) : null });
                              setSyncOverride(res.data);
                              toast.success(`Sync set to: ${opt.label}`);
                            } catch { toast.error('Failed to update sync override'); }
                          }}
                          className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                            (syncOverride?.mode || 'global') === opt.value
                              ? opt.value === 'excluded' ? 'bg-red-50 border-red-300 text-red-700' : 'bg-blue-50 border-blue-300 text-blue-700'
                              : 'bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100'
                          }`}
                          data-testid={`sync-override-${opt.value}`}
                        >
                          {opt.label}
                        </button>
                      ))}
                      {(syncOverride?.mode) === 'custom' && (
                        <Select
                          value={String(syncOverride?.custom_interval_minutes || 15)}
                          onValueChange={async (v) => {
                            try {
                              const res = await api.put(`/assets/${asset.id}/sync-override`, { mode: 'custom', custom_interval_minutes: parseInt(v) });
                              setSyncOverride(res.data);
                              toast.success('Custom interval saved');
                            } catch { toast.error('Failed to update'); }
                          }}
                        >
                          <SelectTrigger className="w-36 h-8" data-testid="sync-override-interval">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="5">Every 5 min</SelectItem>
                            <SelectItem value="15">Every 15 min</SelectItem>
                            <SelectItem value="30">Every 30 min</SelectItem>
                            <SelectItem value="60">Hourly</SelectItem>
                            <SelectItem value="120">Every 2 hrs</SelectItem>
                            <SelectItem value="360">Every 6 hrs</SelectItem>
                            <SelectItem value="720">Every 12 hrs</SelectItem>
                            <SelectItem value="1440">Daily</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                    {(syncOverride?.mode) === 'excluded' && (
                      <p className="text-xs text-red-500 mt-1">This device is excluded from automatic sync</p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* BitDefender Integration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    BitDefender
                  </CardTitle>
                  <Badge className={getIntegrationStatus('bitdefender').color}>
                    {getIntegrationStatus('bitdefender').label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <div>
                    <Label>BitDefender Endpoint ID</Label>
                    <Input
                      value={editForm.bitdefender_endpoint_id}
                      onChange={(e) => setEditForm({...editForm, bitdefender_endpoint_id: e.target.value})}
                      placeholder="Auto-synced from BitDefender"
                      data-testid="bitdefender-id-input"
                      disabled
                    />
                    <p className="text-xs text-gray-500 mt-1">Endpoint ID is auto-linked during BitDefender sync</p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-gray-500">Endpoint ID</Label>
                    <p className="font-mono text-sm">{asset.bitdefender_endpoint_id || 'Not linked'}</p>
                  </div>
                )}

                {bitdefenderLoading ? (
                  <div className="flex items-center justify-center py-4">
                    <Activity className="w-5 h-5 animate-spin text-gray-400" />
                  </div>
                ) : bitdefenderData ? (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4 p-4 bg-gray-50 rounded">
                      <div>
                        <Label className="text-xs text-gray-500">Security Status</Label>
                        <Badge className={
                          bitdefenderData.security_status === 'protected' ? 'bg-green-100 text-green-800' :
                          bitdefenderData.security_status === 'at_risk' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }>
                          {bitdefenderData.security_status?.toUpperCase()}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Antimalware</Label>
                        <Badge variant={bitdefenderData.antimalware_enabled ? 'default' : 'destructive'}>
                          {bitdefenderData.antimalware_enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Product Status</Label>
                        <Badge variant={bitdefenderData.product_outdated ? 'destructive' : 'default'}>
                          {bitdefenderData.product_outdated ? 'Outdated' : 'Current'}
                        </Badge>
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Threats Detected</Label>
                        <p className="text-sm font-semibold text-green-600">{bitdefenderData.threat_count || 0}</p>
                      </div>
                    </div>

                    {bitdefenderData.last_scan_date && (
                      <div className="p-3 bg-blue-50 rounded border border-blue-200">
                        <Label className="text-xs text-gray-600">Last Scan</Label>
                        <p className="text-sm">{new Date(bitdefenderData.last_scan_date).toLocaleString()}</p>
                      </div>
                    )}
                  </div>
                ) : asset.bitdefender_endpoint_id ? (
                  <p className="text-sm text-gray-500 italic">
                    BitDefender data not available. Try syncing from the BitDefender dashboard.
                  </p>
                ) : (
                  <p className="text-sm text-gray-500 italic">
                    No BitDefender endpoint linked. Sync endpoints from BitDefender dashboard to auto-link.
                  </p>
                )}

                {!asset.bitdefender_endpoint_id && !editMode && (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    onClick={() => navigate('/bitdefender')}
                    className="w-full"
                  >
                    Go to BitDefender Dashboard
                  </Button>
                )}
              </CardContent>
            </Card>

            {/* ScreenConnect Integration */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Monitor className="h-5 w-5" />
                    ScreenConnect
                  </CardTitle>
                  <Badge className={getIntegrationStatus('screenconnect').color}>
                    {getIntegrationStatus('screenconnect').label}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent className="space-y-4">
                {editMode ? (
                  <div>
                    <Label>Active Session ID</Label>
                    <Input
                      value={editForm.screenconnect_session_id}
                      onChange={(e) => setEditForm({...editForm, screenconnect_session_id: e.target.value})}
                      placeholder="Session created automatically"
                      disabled
                      data-testid="screenconnect-id-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Sessions are created automatically when you launch remote support</p>
                  </div>
                ) : (
                  <div>
                    <Label className="text-sm text-gray-500">Active Session ID</Label>
                    <p className="font-mono text-sm">{asset.screenconnect_session_id || 'No active session'}</p>
                    {asset.screenconnect_session_status && (
                      <Badge className="mt-1 bg-green-100 text-green-800">
                        {asset.screenconnect_session_status.toUpperCase()}
                      </Badge>
                    )}
                  </div>
                )}

                {asset.screenconnect_session_id && asset.screenconnect_session_status === 'active' ? (
                  <div className="space-y-3">
                    <div className="p-4 bg-green-50 border border-green-200 rounded">
                      <Label className="text-xs text-green-900 font-semibold">Active Remote Session</Label>
                      <p className="text-sm text-green-800 mt-1">A remote session is currently active for this asset</p>
                    </div>
                    
                    <div className="grid grid-cols-2 gap-2">
                      <Button 
                        variant="default"
                        size="sm"
                        onClick={() => {
                          window.open(asset.screenconnect_host_url || screenconnectService.getHostJoinUrl(asset.screenconnect_session_id), '_blank', 'noopener,noreferrer');
                          toast.info('Opening ScreenConnect session...');
                        }}
                        data-testid="join-session-btn"
                      >
                        <Monitor className="h-4 w-4 mr-2" />
                        Join Session
                      </Button>
                      <Button 
                        variant="outline"
                        size="sm"
                        onClick={() => {
                          const url = asset.screenconnect_guest_url || screenconnectService.getGuestJoinUrl(asset.screenconnect_session_id);
                          
                          // Fallback copy method for restricted environments
                          const textarea = document.createElement('textarea');
                          textarea.value = url;
                          textarea.style.position = 'fixed';
                          textarea.style.opacity = '0';
                          document.body.appendChild(textarea);
                          textarea.select();
                          
                          try {
                            document.execCommand('copy');
                            toast.success('Guest link copied to clipboard');
                          } catch (err) {
                            toast.error('Copy failed. Link: ' + url);
                          }
                          
                          document.body.removeChild(textarea);
                        }}
                      >
                        Copy Guest Link
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <p className="text-sm text-gray-600">
                      Create a new remote support session to access this asset remotely
                    </p>
                    <Button 
                      onClick={handleCreateScreenConnectSession}
                      disabled={screenconnectCreating}
                      className="w-full"
                      data-testid="create-screenconnect-session-btn"
                    >
                      {screenconnectCreating ? (
                        <>
                          <Activity className="h-4 w-4 mr-2 animate-spin" />
                          Creating Session...
                        </>
                      ) : (
                        <>
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Launch Remote Support
                        </>
                      )}
                    </Button>
                    <p className="text-xs text-gray-500 italic">
                      Creates a new ScreenConnect session and opens the remote control window
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {asset.last_integration_sync && (
              <p className="text-xs text-gray-500 text-center">
                Last synced: {formatInCST(asset.last_integration_sync, 'MMM d, yyyy h:mm a')}
              </p>
            )}
          </TabsContent>

          {/* Related Tickets Tab */}
          <TabsContent value="tickets" className="space-y-3">
            {loading ? (
              <p className="text-center text-gray-500 py-8">Loading tickets...</p>
            ) : relatedTickets.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Ticket className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                <p>No tickets linked to this asset</p>
              </div>
            ) : (
              relatedTickets.map((ticket) => (
                <Card 
                  key={ticket.id} 
                  className="hover:shadow-md transition-shadow cursor-pointer"
                  onClick={() => {
                    navigate('/tickets');
                    onOpenChange(false);
                  }}
                  data-testid={`asset-ticket-${ticket.id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-sm font-mono text-gray-500">{ticket.ticket_number}</span>
                          <Badge className="text-xs capitalize">{ticket.status}</Badge>
                          <Badge className="text-xs capitalize">{ticket.priority}</Badge>
                        </div>
                        <h4 className="font-semibold">{ticket.title}</h4>
                        {ticket.description && (
                          <p className="text-sm text-gray-600 mt-1 line-clamp-2">{ticket.description}</p>
                        )}
                        <p className="text-xs text-gray-500 mt-2">
                          Created {formatInCST(ticket.created_at, 'MMM d, yyyy')}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </TabsContent>

          {/* Activity Log Tab */}
          <TabsContent value="activities">
            {!asset.ninjarmm_device_id ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8 text-gray-500">
                    <Activity className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p>No NinjaRMM device configured</p>
                    <p className="text-sm mt-1">Configure NinjaRMM integration to view device activities</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Activity className="h-5 w-5" />
                        Device Activity Log
                      </CardTitle>
                      {sectionTimestamps.activities && (
                        <span className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-testid="activities-last-synced">
                          <Clock className="h-3 w-3" /> Cached: {formatInCST(sectionTimestamps.activities, 'MMM d, yyyy h:mm a')}
                        </span>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant={ninjaData.activities.length > 0 ? 'outline' : 'default'}
                      onClick={loadNinjaActivities} 
                      disabled={ninjaLoading.activities}
                      data-testid="load-activities-btn"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.activities ? 'animate-spin' : ''}`} />
                      {ninjaLoading.activities ? 'Refreshing...' : ninjaData.activities.length > 0 ? 'Refresh Activities' : 'Load Activities'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {ninjaLoading.activities ? <p className="py-8 text-muted-foreground">Loading activities…</p> : <ActivityReview activities={ninjaData.activities} />}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* Software Inventory Tab */}
          <TabsContent value="software">
            {!asset.ninjarmm_device_id ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8 text-gray-500">
                    <Download className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p>No NinjaRMM device configured</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardHeader>
                  <div className="flex items-center justify-between">
                    <div>
                      <CardTitle className="text-lg flex items-center gap-2">
                        <Download className="h-5 w-5" />
                        Installed Software
                      </CardTitle>
                      {sectionTimestamps.software && (
                        <span className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-testid="software-last-synced">
                          <Clock className="h-3 w-3" /> Cached: {formatInCST(sectionTimestamps.software, 'MMM d, yyyy h:mm a')}
                        </span>
                      )}
                    </div>
                    <Button 
                      size="sm" 
                      variant={ninjaData.software.length > 0 ? 'outline' : 'default'}
                      onClick={loadNinjaSoftware} 
                      disabled={ninjaLoading.software}
                      data-testid="load-software-btn"
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.software ? 'animate-spin' : ''}`} />
                      {ninjaLoading.software ? 'Refreshing...' : ninjaData.software.length > 0 ? 'Refresh Software' : 'Load Software'}
                    </Button>
                  </div>
                </CardHeader>
                <CardContent>
                  {ninjaLoading.software ? (
                    <p className="text-center py-8 text-gray-500">Loading software inventory...</p>
                  ) : ninjaData.software.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Download className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                      <p>No software data loaded yet</p>
                      <p className="text-sm mt-1">Click "Load Software" to fetch from NinjaRMM</p>
                    </div>
                  ) : (
                    <SoftwareInventory key={asset.id} software={ninjaData.software} />
                  )}
                </CardContent>
              </Card>
            )}
          </TabsContent>

          {/* System Info Tab */}
          <TabsContent value="system">
            {!asset.ninjarmm_device_id ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8 text-gray-500">
                    <Server className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p>No NinjaRMM device configured</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  {sectionTimestamps.processors && (
                    <span className="text-xs text-gray-500 flex items-center gap-1" data-testid="system-info-last-synced">
                      <Clock className="h-3 w-3" /> Cached: {formatInCST(sectionTimestamps.processors, 'MMM d, yyyy h:mm a')}
                    </span>
                  )}
                  {!sectionTimestamps.processors && <span />}
                  <Button 
                    size="sm" 
                    variant={ninjaData.processors.length > 0 ? 'outline' : 'default'}
                    onClick={loadNinjaSystemInfo} 
                    disabled={ninjaLoading.system}
                    data-testid="load-system-info-btn"
                  >
                    <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.system ? 'animate-spin' : ''}`} />
                    {ninjaLoading.system ? 'Refreshing...' : ninjaData.processors.length > 0 ? 'Refresh System Info' : 'Load System Info'}
                  </Button>
                </div>

                {/* Processors */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Cpu className="h-5 w-5" />
                      Processors
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ninjaLoading.system ? (
                      <p className="text-center py-4 text-gray-500">Loading...</p>
                    ) : ninjaData.processors.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No processor data available. Click "Load System Info" to fetch from NinjaRMM.</p>
                    ) : (
                      <div className="space-y-3">
                        {(Array.isArray(ninjaData.processors) ? ninjaData.processors : []).map((proc, idx) => (
                          <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-base text-gray-900">{proc.name || 'Unknown Processor'}</h4>
                                {proc.architecture && (
                                  <Badge className="mt-1 bg-blue-100 text-blue-800 text-xs">
                                    {proc.architecture}
                                  </Badge>
                                )}
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm">
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Physical Cores</Label>
                                <p className="font-medium text-gray-900">{proc.numCores || proc.cores || proc.numberOfCores || 'N/A'}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Logical Cores</Label>
                                <p className="font-medium text-gray-900">{proc.numLogicalCores || proc.threads || 'N/A'}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Base Clock</Label>
                                <p className="font-medium text-gray-900">
                                  {proc.clockSpeed 
                                    ? `${(proc.clockSpeed / 1000000000).toFixed(2)} GHz` 
                                    : proc.speed 
                                    ? `${proc.speed} MHz`
                                    : 'N/A'}
                                </p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Max Clock</Label>
                                <p className="font-medium text-gray-900">
                                  {proc.maxClockSpeed 
                                    ? `${(proc.maxClockSpeed / 1000000000).toFixed(2)} GHz`
                                    : 'N/A'}
                                </p>
                              </div>
                              {proc.manufacturer && (
                                <div className="col-span-2">
                                  <Label className="text-xs text-gray-500 font-semibold">Manufacturer</Label>
                                  <p className="text-sm text-gray-700">{proc.manufacturer}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Disks */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <HardDrive className="h-5 w-5" />
                      Disk Drives
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ninjaLoading.system ? (
                      <p className="text-center py-4 text-gray-500">Loading...</p>
                    ) : ninjaData.disks.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No disk data available</p>
                    ) : (
                      <div className="space-y-3">
                        {(Array.isArray(ninjaData.disks) ? ninjaData.disks : []).map((disk, idx) => (
                          <div key={idx} className="p-4 bg-white rounded-lg border border-gray-200 shadow-sm">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-base text-gray-900">{disk.name || disk.drive || 'Unknown Drive'}</h4>
                                <div className="flex gap-2 mt-1 flex-wrap">
                                  {disk.description && (
                                    <Badge className="bg-blue-100 text-blue-800 text-xs">{disk.description}</Badge>
                                  )}
                                  {disk.mediaType && (
                                    <Badge className="bg-gray-100 text-gray-800 text-xs">{disk.mediaType}</Badge>
                                  )}
                                  {disk.status && (
                                    <Badge className={disk.status === 'OK' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                                      {disk.status}
                                    </Badge>
                                  )}
                                </div>
                              </div>
                            </div>
                            
                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 text-sm mb-3">
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Total Capacity</Label>
                                <p className="font-medium text-gray-900">{disk.size ? `${(disk.size / 1024 / 1024 / 1024).toFixed(2)} GB` : 'N/A'}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Free Space</Label>
                                <p className="font-medium text-gray-900">{disk.freeSpace ? `${(disk.freeSpace / 1024 / 1024 / 1024).toFixed(2)} GB` : 'N/A'}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Model</Label>
                                <p className="font-medium text-gray-900">{disk.model || 'N/A'}</p>
                              </div>
                              <div>
                                <Label className="text-xs text-gray-500 font-semibold">Interface</Label>
                                <p className="font-medium text-gray-900">{disk.interfaceType || 'N/A'}</p>
                              </div>
                              <div className="col-span-2">
                                <Label className="text-xs text-gray-500 font-semibold">Serial Number</Label>
                                <p className="font-mono text-xs text-gray-700">{disk.serialNumber || 'N/A'}</p>
                              </div>
                              {disk.partitionCount && (
                                <div>
                                  <Label className="text-xs text-gray-500 font-semibold">Partitions</Label>
                                  <p className="font-medium text-gray-900">{disk.partitionCount}</p>
                                </div>
                              )}
                              {disk.bytesPerSector && (
                                <div>
                                  <Label className="text-xs text-gray-500 font-semibold">Sector Size</Label>
                                  <p className="font-medium text-gray-900">{disk.bytesPerSector} bytes</p>
                                </div>
                              )}
                            </div>
                            
                            {disk.size && disk.freeSpace && (
                              <div className="mt-3">
                                <div className="flex items-center justify-between text-xs text-gray-600 mb-1">
                                  <span>Disk Usage</span>
                                  <span className="font-semibold">
                                    {((disk.size - disk.freeSpace) / 1024 / 1024 / 1024).toFixed(2)} GB used / {(disk.size / 1024 / 1024 / 1024).toFixed(2)} GB total
                                  </span>
                                </div>
                                <div className="flex items-center gap-2">
                                  <div className="flex-1 bg-gray-200 rounded-full h-3">
                                    <div 
                                      className={`h-3 rounded-full transition-all ${
                                        ((disk.size - disk.freeSpace) / disk.size * 100) > 90 ? 'bg-red-600' :
                                        ((disk.size - disk.freeSpace) / disk.size * 100) > 75 ? 'bg-orange-500' :
                                        'bg-blue-600'
                                      }`}
                                      style={{ width: `${((disk.size - disk.freeSpace) / disk.size * 100).toFixed(1)}%` }}
                                    />
                                  </div>
                                  <span className="text-xs font-semibold text-gray-700 min-w-[50px] text-right">
                                    {((disk.size - disk.freeSpace) / disk.size * 100).toFixed(1)}%
                                  </span>
                                </div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Network Interfaces */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Network className="h-5 w-5" />
                      Network Interfaces
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {ninjaLoading.system ? (
                      <p className="text-center py-4 text-gray-500">Loading...</p>
                    ) : ninjaData.networkInterfaces.length === 0 ? (
                      <p className="text-sm text-gray-500 italic">No network interface data available</p>
                    ) : (
                      <div className="space-y-3">
                        {(Array.isArray(ninjaData.networkInterfaces) ? ninjaData.networkInterfaces : []).map((iface, idx) => {
                          // Separate IPv4 and IPv6 addresses
                          const ipAddresses = Array.isArray(iface.ipAddress) ? iface.ipAddress : 
                                            (iface.ipAddress ? [iface.ipAddress] : []);
                          const ipv4Addresses = ipAddresses.filter(ip => !ip.includes(':'));
                          const ipv6Addresses = ipAddresses.filter(ip => ip.includes(':'));
                          
                          return (
                            <div key={idx} className="p-3 bg-gray-50 rounded border">
                              <div className="grid grid-cols-2 gap-3 text-sm">
                                <div className="col-span-2">
                                  <Label className="text-xs text-gray-500">Interface Name</Label>
                                  <p className="font-medium">{iface.interfaceName || iface.name || iface.description || 'N/A'}</p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">MAC Address</Label>
                                  <p className="font-mono text-xs">
                                    {Array.isArray(iface.macAddress) ? iface.macAddress[0] : (iface.macAddress || iface.mac || 'N/A')}
                                  </p>
                                </div>
                                <div>
                                  <Label className="text-xs text-gray-500">Status</Label>
                                  <Badge className={
                                    iface.status === 'Up' || iface.status === 'Connected' || iface.enabled
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-gray-100 text-gray-800'
                                  }>
                                    {iface.status || (iface.enabled ? 'Active' : 'Inactive')}
                                  </Badge>
                                </div>
                                
                                {ipv4Addresses.length > 0 && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500 flex items-center gap-1">
                                      <Network className="h-3 w-3" />
                                      IPv4 Address{ipv4Addresses.length > 1 ? 'es' : ''}
                                    </Label>
                                    <div className="space-y-1">
                                      {ipv4Addresses.map((ip, ipIdx) => (
                                        <p key={ipIdx} className="font-mono text-xs bg-blue-50 px-2 py-1 rounded border border-blue-200">
                                          {ip}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {ipv6Addresses.length > 0 && (
                                  <div className="col-span-2">
                                    <Label className="text-xs text-gray-500 flex items-center gap-1">
                                      <Network className="h-3 w-3" />
                                      IPv6 Address{ipv6Addresses.length > 1 ? 'es' : ''}
                                    </Label>
                                    <div className="space-y-1">
                                      {ipv6Addresses.map((ip, ipIdx) => (
                                        <p key={ipIdx} className="font-mono text-xs bg-purple-50 px-2 py-1 rounded border border-purple-200 break-all">
                                          {ip}
                                        </p>
                                      ))}
                                    </div>
                                  </div>
                                )}
                                
                                {iface.interfaceType && (
                                  <div>
                                    <Label className="text-xs text-gray-500">Type</Label>
                                    <p className="text-xs">{iface.interfaceType}</p>
                                  </div>
                                )}
                                {iface.linkSpeed && (
                                  <div>
                                    <Label className="text-xs text-gray-500">Link Speed</Label>
                                    <p className="text-xs">{(parseInt(iface.linkSpeed) / 1000000).toFixed(0)} Mbps</p>
                                  </div>
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Alerts */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <CardTitle className="text-lg flex items-center gap-2">
                        <AlertTriangle className="h-5 w-5" />
                        Active Alerts
                      </CardTitle>
                      <Button 
                        size="sm" 
                        onClick={loadNinjaAlerts} 
                        disabled={ninjaLoading.alerts}
                        data-testid="load-alerts-btn"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.alerts ? 'animate-spin' : ''}`} />
                        {ninjaLoading.alerts ? 'Loading...' : 'Refresh Alerts'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {ninjaLoading.alerts ? (
                      <p className="text-center py-4 text-gray-500">Loading alerts...</p>
                    ) : ninjaData.alerts.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <CheckCircle className="h-8 w-8 mx-auto text-green-500 mb-2" />
                        <p className="text-sm">No active alerts</p>
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {(Array.isArray(ninjaData.alerts) ? ninjaData.alerts : []).map((alert, idx) => (
                          <Card key={idx} className="border-l-4 border-l-red-500">
                            <CardContent className="p-3">
                              <div className="flex items-start justify-between">
                                <div className="flex-1">
                                  <p className="font-medium text-sm">{alert.message || alert.title || 'Alert'}</p>
                                  <p className="text-xs text-gray-600 mt-1">{alert.description || 'No description'}</p>
                                  <p className="text-xs text-gray-400 mt-1">
                                    {alert.createTime ? formatInCST(alert.createTime, 'MMM d, yyyy h:mm a') : 'Unknown time'}
                                  </p>
                                </div>
                                {alert.priority && (
                                  <Badge className={
                                    alert.priority === 'CRITICAL' ? 'bg-red-100 text-red-800' :
                                    alert.priority === 'HIGH' ? 'bg-orange-100 text-orange-800' :
                                    'bg-yellow-100 text-yellow-800'
                                  }>
                                    {alert.priority}
                                  </Badge>
                                )}
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Remote Tools Tab */}
          <TabsContent value="tools">
            {!asset.ninjarmm_device_id ? (
              <Card>
                <CardContent className="p-6">
                  <div className="text-center py-8 text-gray-500">
                    <Terminal className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                    <p>No NinjaRMM device configured</p>
                    <p className="text-sm mt-1">Configure NinjaRMM integration to access remote tools</p>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {/* Dashboard Link & Remote Access */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Monitor className="h-5 w-5" />
                      Remote Access & Management
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div>
                      <Label className="text-sm font-semibold text-gray-700 mb-2 block">NinjaRMM Dashboard</Label>
                      <p className="text-sm text-gray-600 mb-3">
                        Open this device in NinjaRMM for remote control, advanced diagnostics, and management tools
                      </p>
                      <Button 
                        onClick={async () => {
                          const deviceId = asset.ninjarmm_device_id;
                          const dashboardUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview`;
                          window.open(dashboardUrl, '_blank');
                        }}
                        className="w-full sm:w-auto"
                        data-testid="open-dashboard-btn"
                      >
                        <ExternalLink className="h-4 w-4 mr-2" />
                        Open in NinjaRMM Dashboard
                      </Button>
                    </div>
                    
                    <Separator />
                    
                    <div>
                      <Label className="text-sm font-semibold text-gray-700 mb-2 block">Quick Remote Actions</Label>
                      <p className="text-sm text-gray-600 mb-3">
                        Open device dashboard in NinjaRMM to access remote tools
                      </p>
                      <div className="grid grid-cols-3 gap-2">
                        <Button 
                          variant="outline"
                          onClick={() => {
                            const deviceId = asset.ninjarmm_device_id;
                            const dashboardUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview`;
                            window.open(dashboardUrl, '_blank', 'noopener,noreferrer');
                            toast.info('Opening device dashboard - click Remote Desktop from the Actions menu');
                          }}
                          data-testid="dashboard-btn"
                        >
                          <ExternalLink className="h-4 w-4 mr-2" />
                          Dashboard
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            const deviceId = asset.ninjarmm_device_id;
                            // Try the remote control view - may need to manually click "Connect" button once there
                            const remoteUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview?action=remote`;
                            window.open(remoteUrl, '_blank', 'noopener,noreferrer');
                            toast.info('Opening dashboard - Click "Remote Desktop" button in the Actions panel');
                          }}
                          data-testid="remote-control-btn"
                        >
                          <Monitor className="h-4 w-4 mr-2" />
                          Remote
                        </Button>
                        <Button 
                          variant="outline"
                          onClick={() => {
                            const deviceId = asset.ninjarmm_device_id;
                            // Try the terminal view
                            const terminalUrl = `https://mathewsit.rmmservice.com/#/deviceDashboard/${deviceId}/overview?action=terminal`;
                            window.open(terminalUrl, '_blank', 'noopener,noreferrer');
                            toast.info('Opening dashboard - Click "Terminal" button in the Actions panel');
                          }}
                          data-testid="terminal-btn"
                        >
                          <Terminal className="h-4 w-4 mr-2" />
                          Terminal
                        </Button>
                      </div>
                      <div className="mt-3 p-3 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-xs text-blue-900 font-semibold mb-1">📌 How to access remote tools:</p>
                        <ol className="text-xs text-blue-800 space-y-1 ml-4 list-decimal">
                          <li>Button opens the device dashboard in NinjaRMM</li>
                          <li>Look for the <strong>Actions</strong> panel on the right side</li>
                          <li>Click <strong>"Remote Desktop"</strong> or <strong>"Terminal"</strong> button</li>
                          <li>Remote session will launch in a new window</li>
                        </ol>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Reboot Controls */}
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <Power className="h-5 w-5" />
                      Reboot Device
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-sm text-gray-600 mb-4">
                      Remotely restart this device. Use Safe Mode options if normal boot fails.
                    </p>
                    <div className="flex gap-2 flex-wrap">
                      <Button 
                        variant="outline"
                        onClick={() => handleRebootDevice('NORMAL')} 
                        disabled={rebooting}
                        data-testid="reboot-normal-btn"
                      >
                        <Power className="h-4 w-4 mr-2" />
                        Normal Reboot
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleRebootDevice('SAFE')} 
                        disabled={rebooting}
                        data-testid="reboot-safe-btn"
                      >
                        <Shield className="h-4 w-4 mr-2" />
                        Safe Mode
                      </Button>
                      <Button 
                        variant="outline"
                        onClick={() => handleRebootDevice('SAFE_WITH_NETWORKING')} 
                        disabled={rebooting}
                        data-testid="reboot-safe-network-btn"
                      >
                        <Network className="h-4 w-4 mr-2" />
                        Safe Mode + Network
                      </Button>
                    </div>
                    {rebooting && (
                      <p className="text-sm text-blue-600 mt-3">Sending reboot command...</p>
                    )}
                  </CardContent>
                </Card>

                {/* Windows Services */}
                <Card>
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-lg flex items-center gap-2">
                          <Play className="h-5 w-5" />
                          Windows Services
                        </CardTitle>
                        {sectionTimestamps.services && (
                          <span className="text-xs text-gray-500 flex items-center gap-1 mt-1" data-testid="services-last-synced">
                            <Clock className="h-3 w-3" /> Cached: {formatInCST(sectionTimestamps.services, 'MMM d, yyyy h:mm a')}
                          </span>
                        )}
                      </div>
                      <Button 
                        size="sm" 
                        variant={ninjaData.services.length > 0 ? 'outline' : 'default'}
                        onClick={loadNinjaServices} 
                        disabled={ninjaLoading.services}
                        data-testid="load-services-btn"
                      >
                        <RefreshCw className={`h-4 w-4 mr-2 ${ninjaLoading.services ? 'animate-spin' : ''}`} />
                        {ninjaLoading.services ? 'Refreshing...' : ninjaData.services.length > 0 ? 'Refresh Services' : 'Load Services'}
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    {ninjaLoading.services ? (
                      <p className="text-center py-4 text-gray-500">Loading services...</p>
                    ) : ninjaData.services.length === 0 ? (
                      <div className="text-center py-4 text-gray-500">
                        <Play className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                        <p className="text-sm">No services data loaded</p>
                        <p className="text-xs mt-1">Click "Load Services" to fetch from NinjaRMM</p>
                      </div>
                    ) : (
                      <div className="max-h-[300px] overflow-y-auto">
                        <Table>
                          <TableHeader>
                            <TableRow>
                              <TableHead>Service Name</TableHead>
                              <TableHead>Display Name</TableHead>
                              <TableHead>Status</TableHead>
                              <TableHead>Startup Type</TableHead>
                              <TableHead>Actions</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {(Array.isArray(ninjaData.services) ? ninjaData.services : []).map((service, idx) => {
                              const serviceName = service.name || '';
                              const isRunning = service.status === 'Running' || service.state === 'Running';
                              const isManaging = managingService?.startsWith(serviceName);
                              
                              return (
                                <TableRow key={idx}>
                                  <TableCell className="font-mono text-xs">{serviceName || 'N/A'}</TableCell>
                                  <TableCell className="text-sm">{service.displayName || service.description || 'N/A'}</TableCell>
                                  <TableCell>
                                    <Badge 
                                      variant="custom"
                                      className={
                                        isRunning
                                          ? 'bg-green-100 text-green-800 hover:bg-green-200 transition-colors' 
                                          : 'bg-gray-100 text-gray-800 hover:bg-gray-200 transition-colors'
                                      }
                                    >
                                      {service.status || service.state || 'Unknown'}
                                    </Badge>
                                  </TableCell>
                                  <TableCell className="text-sm capitalize">
                                    {service.startupType || service.startMode || 'N/A'}
                                  </TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleManageService(serviceName, 'start')}
                                        disabled={isRunning || isManaging}
                                        className="h-7 px-2 text-xs"
                                        title="Start service"
                                      >
                                        <PlayCircle className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleManageService(serviceName, 'stop')}
                                        disabled={!isRunning || isManaging}
                                        className="h-7 px-2 text-xs"
                                        title="Stop service"
                                      >
                                        <StopCircle className="h-3 w-3" />
                                      </Button>
                                      <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={() => handleManageService(serviceName, 'restart')}
                                        disabled={!isRunning || isManaging}
                                        className="h-7 px-2 text-xs"
                                        title="Restart service"
                                      >
                                        <RefreshCw className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                          </TableBody>
                        </Table>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </div>
            )}
          </TabsContent>

          {/* Keep existing Activity Log tab but rename to avoid confusion */}
          <TabsContent value="activity-old" style={{ display: 'none' }}>
            <Card>
              <CardContent className="p-6">
                <div className="text-center py-8 text-gray-500">
                  <Activity className="h-12 w-12 mx-auto text-gray-400 mb-2" />
                  <p>Activity logging coming soon</p>
                  <p className="text-sm mt-1">Track who viewed and edited this asset</p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* RMM Tabs (Phase 1F) */}
          <TabsContent value="rmm-hardware">{asset.ninjarmm_device_id && <RmmHardwareTab assetId={asset.id} />}</TabsContent>
          <TabsContent value="rmm-software">{asset.ninjarmm_device_id && <RmmSoftwareTab assetId={asset.id} />}</TabsContent>
          <TabsContent value="rmm-services">{asset.ninjarmm_device_id && <RmmServicesTab assetId={asset.id} />}</TabsContent>
          <TabsContent value="rmm-alerts">{asset.ninjarmm_device_id && <RmmAlertsTab assetId={asset.id} />}</TabsContent>
          <TabsContent value="rmm-activities">{asset.ninjarmm_device_id && <RmmActivitiesTab assetId={asset.id} />}</TabsContent>
          <TabsContent value="rmm-patches">{asset.ninjarmm_device_id && <RmmPatchesTab />}</TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );
}
